import { SignJWT, jwtVerify } from 'jose';
import { browserDB, type User, type Session } from '../browser-storage/indexedDB';
import { sessionStorage } from '../browser-storage/localStorage';

export interface AuthUser {
  id: number;
  email: string;
  name?: string;
  avatar?: string;
  // Legacy properties for backward compatibility
  displayName?: string;
  username?: string;
  bio?: string;
  timezone?: string;
  provider?: string;
  avatarUrl?: string;
  emailVerified?: boolean;
  isAnonymous?: boolean;
  createdAt?: Date;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterCredentials {
  email: string;
  password: string;
  name?: string;
}

export interface AuthResult {
  success: boolean;
  user?: AuthUser;
  token?: string;
  error?: string;
}

export class ClientAuthService {
  private readonly jwtSecret = new TextEncoder().encode('app-builder-client-secret-key-32-bytes-min');
  private readonly tokenExpiry = 7 * 24 * 60 * 60 * 1000; // 7 days
  private currentUser: AuthUser | null = null;
  private currentToken: string | null = null;

  constructor() {
    this.initializeAuth();
  }

  /**
   * Initialize authentication state from stored session
   */
  private async initializeAuth(): Promise<void> {
    try {
      const token = await sessionStorage.get<string>('auth_token');
      if (token) {
        const user = await this.verifyToken(token);
        if (user) {
          this.currentUser = user;
          this.currentToken = token;
        } else {
          await this.logout();
        }
      }
    } catch (error) {
      console.warn('Failed to initialize auth state:', error);
      await this.logout();
    }
  }

  /**
   * Register a new user
   */
  async register(credentials: RegisterCredentials): Promise<AuthResult> {
    try {
      // Check if user already exists
      const existingUser = await browserDB.getUserByEmail(credentials.email);
      if (existingUser) {
        return {
          success: false,
          error: 'User with this email already exists'
        };
      }

      // Hash password using Web Crypto API
      const hashedPassword = await this.hashPassword(credentials.password);

      // Create user
      const user = await browserDB.createUser({
        email: credentials.email,
        name: credentials.name,
        avatar: this.generateAvatar(credentials.email)
      });

      // Store password hash separately (in a real app, this would be more secure)
      await sessionStorage.put(`password_${user.id}`, hashedPassword, { expiresIn: 365 * 24 * 60 * 60 * 1000 });

      // Create session
      const token = await this.createToken(user);
      await this.createSession(user.id!, token);

      // Set current user
      this.currentUser = {
        id: user.id!,
        email: user.email,
        name: user.name,
        avatar: user.avatar
      };
      this.currentToken = token;

      // Store token
      await sessionStorage.put('auth_token', token, { expiresIn: this.tokenExpiry });

      return {
        success: true,
        user: this.currentUser,
        token
      };
    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Registration failed'
      };
    }
  }

  /**
   * Login with email and password
   */
  async login(credentials: LoginCredentials): Promise<AuthResult> {
    try {
      // Find user
      const user = await browserDB.getUserByEmail(credentials.email);
      if (!user) {
        return {
          success: false,
          error: 'Invalid email or password'
        };
      }

      // Verify password
      const storedHash = await sessionStorage.get<string>(`password_${user.id}`);
      if (!storedHash) {
        return {
          success: false,
          error: 'Invalid email or password'
        };
      }

      const isValidPassword = await this.verifyPassword(credentials.password, storedHash);
      if (!isValidPassword) {
        return {
          success: false,
          error: 'Invalid email or password'
        };
      }

      // Create session
      const token = await this.createToken(user);
      await this.createSession(user.id!, token);

      // Set current user
      this.currentUser = {
        id: user.id!,
        email: user.email,
        name: user.name,
        avatar: user.avatar
      };
      this.currentToken = token;

      // Store token
      await sessionStorage.put('auth_token', token, { expiresIn: this.tokenExpiry });

      return {
        success: true,
        user: this.currentUser,
        token
      };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Login failed'
      };
    }
  }

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    try {
      // Delete session from database
      if (this.currentToken) {
        const session = await browserDB.getSessionByToken(this.currentToken);
        if (session) {
          await browserDB.deleteSession(session.id!);
        }
      }

      // Clear stored token
      await sessionStorage.delete('auth_token');

      // Clear current user
      this.currentUser = null;
      this.currentToken = null;
    } catch (error) {
      console.error('Logout error:', error);
    }
  }

  /**
   * Get current authenticated user
   */
  getCurrentUser(): AuthUser | null {
    return this.currentUser;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.currentUser !== null && this.currentToken !== null;
  }

  /**
   * Get current auth token
   */
  getToken(): string | null {
    return this.currentToken;
  }

  /**
   * Verify a JWT token
   */
  private async verifyToken(token: string): Promise<AuthUser | null> {
    try {
      const { payload } = await jwtVerify(token, this.jwtSecret);
      
      if (typeof payload.userId !== 'number') {
        return null;
      }

      // Check if session exists in database
      const session = await browserDB.getSessionByToken(token);
      if (!session || session.expiresAt < new Date()) {
        return null;
      }

      // Get user data
      const user = await browserDB.getUserById(payload.userId);
      if (!user) {
        return null;
      }

      return {
        id: user.id!,
        email: user.email,
        name: user.name,
        avatar: user.avatar
      };
    } catch (error) {
      console.warn('Token verification failed:', error);
      return null;
    }
  }

  /**
   * Create a JWT token for a user
   */
  private async createToken(user: User): Promise<string> {
    const token = await new SignJWT({ userId: user.id })
      .setProtectedHeader({ alg: 'HS256' })
      .setIssuedAt()
      .setExpirationTime(new Date(Date.now() + this.tokenExpiry))
      .sign(this.jwtSecret);

    return token;
  }

  /**
   * Create a session in the database
   */
  private async createSession(userId: number, token: string): Promise<Session> {
    // Clean up expired sessions first
    await browserDB.deleteExpiredSessions();

    return await browserDB.createSession({
      userId,
      token,
      expiresAt: new Date(Date.now() + this.tokenExpiry)
    });
  }

  /**
   * Hash a password using Web Crypto API
   */
  private async hashPassword(password: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Verify a password against its hash
   */
  private async verifyPassword(password: string, hash: string): Promise<boolean> {
    const passwordHash = await this.hashPassword(password);
    return passwordHash === hash;
  }

  /**
   * Generate a simple avatar URL based on email
   */
  private generateAvatar(email: string): string {
    const hash = email.toLowerCase().trim();
    return `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(hash)}`;
  }

  /**
   * Update user profile
   */
  async updateProfile(updates: Partial<Pick<User, 'name' | 'avatar'>>): Promise<AuthResult> {
    try {
      if (!this.currentUser) {
        return {
          success: false,
          error: 'Not authenticated'
        };
      }

      await browserDB.updateUser(this.currentUser.id, updates);

      // Update current user
      this.currentUser = {
        ...this.currentUser,
        ...updates
      };

      return {
        success: true,
        user: this.currentUser
      };
    } catch (error) {
      console.error('Profile update error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Profile update failed'
      };
    }
  }

  /**
   * Change password
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<AuthResult> {
    try {
      if (!this.currentUser) {
        return {
          success: false,
          error: 'Not authenticated'
        };
      }

      // Verify current password
      const storedHash = await sessionStorage.get<string>(`password_${this.currentUser.id}`);
      if (!storedHash) {
        return {
          success: false,
          error: 'Current password verification failed'
        };
      }

      const isValidPassword = await this.verifyPassword(currentPassword, storedHash);
      if (!isValidPassword) {
        return {
          success: false,
          error: 'Current password is incorrect'
        };
      }

      // Hash new password
      const newHash = await this.hashPassword(newPassword);
      await sessionStorage.put(`password_${this.currentUser.id}`, newHash, { expiresIn: 365 * 24 * 60 * 60 * 1000 });

      return {
        success: true,
        user: this.currentUser
      };
    } catch (error) {
      console.error('Password change error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Password change failed'
      };
    }
  }

  /**
   * Delete user account
   */
  async deleteAccount(): Promise<AuthResult> {
    try {
      if (!this.currentUser) {
        return {
          success: false,
          error: 'Not authenticated'
        };
      }

      const userId = this.currentUser.id;

      // Delete user data
      await browserDB.deleteSession(userId);
      await sessionStorage.delete(`password_${userId}`);
      
      // Note: In a real app, you'd also delete related data (projects, etc.)
      
      // Logout
      await this.logout();

      return {
        success: true
      };
    } catch (error) {
      console.error('Account deletion error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Account deletion failed'
      };
    }
  }
}

// Export singleton instance
export const clientAuth = new ClientAuthService();
